import { Dialog } from "@capacitor/dialog";
import { Box, Divider, Link, List, ListItem, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { SUPPORT_TELEPHONE, SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";
// eslint-disable-next-line no-restricted-imports -- Need Firebase for logout
import { FirebaseAuth } from "@src/utils/firebase";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { MobilePageWrapper } from "../layouts/MobilePageWrapper";
import { Platform } from "../utils/platform";

interface MenuItemProps {
  label: string;
  value: string | undefined;
  showDivider?: boolean;
}

interface ClickableMenuItemProps {
  label: string;
  value: string | undefined;
  href?: string;
  showDivider?: boolean;
}

function MenuItem({ label, value, showDivider = true }: MenuItemProps) {
  return (
    <>
      <ListItem
        sx={{
          display: "flex",
          justifyContent: "space-between",
          paddingY: 4,
          paddingX: 5,
        }}
      >
        <Text variant="body1" sx={{ color: "text.secondary" }}>
          {label}
        </Text>
        <Text
          variant="body1"
          sx={{
            fontWeight: 500,
            textAlign: "right",
            maxWidth: "60%",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {value ?? "—"}
        </Text>
      </ListItem>
      {showDivider && <Divider />}
    </>
  );
}

function ClickableMenuItem({ label, value, href, showDivider = true }: ClickableMenuItemProps) {
  return (
    <>
      <ListItem
        sx={{
          display: "flex",
          justifyContent: "space-between",
          paddingY: 4,
          paddingX: 5,
        }}
      >
        <Text variant="body1" sx={{ color: "text.secondary" }}>
          {label}
        </Text>
        {href ? (
          <Link
            href={href}
            underline="none"
            sx={{
              fontWeight: 500,
              textAlign: "right",
              maxWidth: "60%",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              color: "primary.main",
            }}
          >
            <Text variant="body1" sx={{ color: "inherit" }}>
              {value ?? "—"}
            </Text>
          </Link>
        ) : (
          <Text
            variant="body1"
            sx={{
              fontWeight: 500,
              textAlign: "right",
              maxWidth: "60%",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {value ?? "—"}
          </Text>
        )}
      </ListItem>
      {showDivider && <Divider />}
    </>
  );
}

function getDeviceType(): string {
  if (Platform.isIOS) {
    return "iOS";
  }

  if (Platform.isAndroid) {
    return "Android";
  }

  return "Web";
}

/**
 * Mobile profile view with user information and account settings
 */
export function MobileProfile() {
  const history = useHistory();
  const session = useSession();

  const handleLogout = useCallback(async () => {
    try {
      // Show native confirmation dialog
      const { value } = await Dialog.confirm({
        title: "Confirm Logout",
        message: "Are you sure you want to logout?",
        okButtonTitle: "Logout",
        cancelButtonTitle: "Cancel",
      });

      // If user confirmed, proceed with logout
      if (value) {
        await FirebaseAuth.signOut();
        // Clear any local storage
        localStorage.clear();
        sessionStorage.clear();
        // Navigate to login
        history.replace("/mobile/login");
      }
    } catch {
      // Handle dialog or logout error
    }
  }, [history]);

  const { user, profile, admin: isAdmin } = session;

  return (
    <MobilePageWrapper>
      {/* Header */}
      <Box sx={{ paddingY: 5, paddingBottom: 3 }}>
        <Title bold component="h5" variant="h5">
          Profile
        </Title>
      </Box>

      <Stack spacing={4}>
        {/* User Details Section */}
        <Box>
          <Box sx={{ paddingY: 2 }}>
            <Text bold variant="body2" sx={{ color: "text.secondary", textTransform: "uppercase" }}>
              User Details
            </Text>
          </Box>
          <Box sx={{ backgroundColor: "background.paper" }}>
            <List disablePadding>
              <MenuItem label="Name" value={user?.name ?? profile.name} />
              <MenuItem label="Email" value={user?.email} showDivider={false} />
            </List>
          </Box>
        </Box>

        {/* Account Settings Section */}
        <Box>
          <Box sx={{ paddingY: 2 }}>
            <Text bold variant="body2" sx={{ color: "text.secondary", textTransform: "uppercase" }}>
              Account Settings
            </Text>
          </Box>
          <Box sx={{ backgroundColor: "background.paper" }}>
            <List disablePadding>
              <MenuItem label="Role" value={isAdmin ? "Admin" : "User"} />
              <MenuItem label="User ID" value={user?._id ?? profile.userId} showDivider={false} />
            </List>
          </Box>
        </Box>

        {/* System Information Section */}
        <Box>
          <Box sx={{ paddingY: 2 }}>
            <Text bold variant="body2" sx={{ color: "text.secondary", textTransform: "uppercase" }}>
              System Information
            </Text>
          </Box>
          <Box sx={{ backgroundColor: "background.paper" }}>
            <List disablePadding>
              <MenuItem label="Device Type" value={getDeviceType()} />
              <MenuItem label="App Version" value={__APP_VERSION__} showDivider={false} />
            </List>
          </Box>
        </Box>

        {/* Support & Help Section */}
        <Box>
          <Box sx={{ paddingY: 2 }}>
            <Text bold variant="body2" sx={{ color: "text.secondary", textTransform: "uppercase" }}>
              Support & Help
            </Text>
          </Box>
          <Box sx={{ backgroundColor: "background.paper" }}>
            <List disablePadding>
              <ClickableMenuItem
                label="Phone Support"
                value={SUPPORT_TELEPHONE}
                href={`tel:${SUPPORT_TELEPHONE}`}
              />
              <ClickableMenuItem
                label="Create Support Ticket"
                value="Open"
                href={SUPPORT_WEBFORM_URL}
                showDivider={false}
              />
            </List>
          </Box>
        </Box>

        {/* Logout Button */}
        <Box sx={{ paddingTop: 4, paddingBottom: 6 }}>
          <Button fullWidth variant="secondary" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </Stack>
    </MobilePageWrapper>
  );
}
